// probe-display.js
//
// How a probe reads to a person, given which probe they picked during setup.
//
// The alerts review at the end of onboarding lists the reading types that will
// be watched. "Probe" is the API's name for the thermocouple input, but the
// person just chose a pipe clamp, a pipe strap, or a wine bottle a few screens
// earlier — so the tile is named after that choice and carries the matching
// glyph. "Temperature" gets the same treatment: next to a probe reading the
// sensor's own reading is the ambient one, and saying so keeps the two apart.
//
// The selection is the option picked on the probe step (the UI key, not the
// thermocouple type it persists as): CLAMP, VELCRO_STRAP, WINE_BOTTLE,
// BUFFER_BOTTLE, or a bare type letter for the "other probe types" list.

import { READING_TYPE_META } from './alert-groups'

// icon names are from util/icon.js
export const PROBE_SELECTION_META = {
  CLAMP: { title: 'Pipe Clamp Temperature', icon: 'pipe' },
  VELCRO_STRAP: { title: 'Pipe Strap Temperature', icon: 'pipe' },
  WINE_BOTTLE: { title: 'Wine Bottle Temperature', icon: 'wine-bottle' },
  // a glycol bottle for a vaccine fridge; no glyph of its own, so it keeps the
  // generic probe one
  BUFFER_BOTTLE: { title: 'Buffer Bottle Temperature', icon: 'microchip' },
};

// a bare thermocouple type (K, T, J...) or no answer at all
export const GENERIC_PROBE_META = { title: 'Probe Temperature', icon: 'microchip' };

export const AMBIENT_TEMP_META = { title: 'Ambient Temperature', icon: 'temperature-half' };

/**
 * Title + icon for the probe reading, for the given probe selection.
 *
 * @param {string} selection  probe-step option key, or '' when none was made
 * @returns {{title: string, icon: string}}
 */
export const probeAlertMeta = selection => {
  const key = String(selection || '').trim().toUpperCase();
  return PROBE_SELECTION_META[key] || GENERIC_PROBE_META;
};

/**
 * Reading-type meta overrides for the onboarding alerts review, shaped for
 * groupAlertsByType's third argument.
 */
export const onboardingReadingTypeMeta = selection => ({
  temperature: { ...READING_TYPE_META.temperature, ...AMBIENT_TEMP_META },
  probe_temp: { ...READING_TYPE_META.probe_temp, ...probeAlertMeta(selection) },
});

/**
 * Best guess at the probe selection from a sensor record's probe meta, for a
 * review reached without the probe step on hand (a reload, or a sensor whose
 * probe was set up on an earlier visit). TC_SKIN is the option the API stored
 * from the probe step; a DEFAULT skin means a bare thermocouple type.
 *
 * Note the API folds the velcro strap into CLAMP, so a reload after picking the
 * strap reads "Pipe Clamp" — the same probe, mounted differently.
 */
export const probeSelectionFromSensor = sensor => {

  if (!sensor) {
    return '';
  }

  const skin = String(sensor.TC_SKIN || sensor.probe_skin || '').trim().toUpperCase();

  if (skin && skin !== 'DEFAULT') {
    return skin;
  }

  return String(sensor.TC_TYPE || sensor.probe_type || '').trim().toUpperCase();

};

export default probeAlertMeta;
